import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./packageContentsViewer.html";
import { aiAssetEngagement, aiAssetSearch } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { WireI18n } from '~/modules/stores/i18nStore';
import { images } from '~/modules/utilities/assetUtilities';
import { debounce } from '~/modules/utilities/browser';
import { CookieConsentCategory, createCatCookie, getCookieConsented } from '~/modules/utilities/cookies';
// Matches the debounce window ace/filterSearch uses for its own filterKeywordChange analytics.
const SEARCH_ANALYTICS_DEBOUNCE_MS = 300;

// This viewer is shared by skillFoundIn and pluginSpecs, so persisting the mode here makes it
// carry across both tabs and reloads for free. Gated Functional — same category as the other
// persisted-but-not-essential prefs (orgContextStore's selected org, recentItemService) — so an
// opted-out user just re-toggles each visit instead of persisting.
const VIEW_MODE_COOKIE_NAME = 'appx_package_contents_view_mode';
const VIEW_MODE_COOKIE_EXPIRY_DAYS = 30;

// Field initializers run before any lifecycle hook, and this component can construct where
// `window`/`document` don't exist — guard before touching the cookie utilities, which assume a
// browser global.
// List is the default (SSR and no cookie); the cookie only ever switches this to card view.
function readInitialViewMode() {
  if (typeof window === 'undefined') {
    return 'list';
  }
  return getCookieConsented(VIEW_MODE_COOKIE_NAME, CookieConsentCategory.Functional) === 'card' ? 'card' : 'list';
}

// Matches wds-listing-card's PLUGIN_BADGE_ICON / SKILL_BADGE_ICON, so the list-view type
// pill can key off the same value to match the card view's badge color exactly.
const PLUGIN_TYPE_ICON = 'apex_plugin';
const SKILL_TYPE_ICON = 'work_order_type';

// Skeleton placeholder counts while isLoading is true: enough card-grid rows to fill the
// viewport without over-rendering, and ~3 list rows per the design's loading mock.
const CARD_SKELETON_COUNT = 6;
const ROW_SKELETON_COUNT = 3;

// View model for a rendered item: adds the search haystack, the list-row combined
// subtitle line, and iteration keys — all derived once here rather than in the template.

/**
 * Presentational, data-driven "package contents" viewer: a searchable, list/card-toggled
 * catalog of items grouped into sections, with a roll-up tile strip above the content.
 * Reused for a plugin's Specs tab and a skill's "Found In" tab — no taxonomy or data-fetching
 * lives here; copy renders from `@api` inputs, except the no-results empty state, which both
 * callers already source from the same i18n selector this component wires directly.
 */
class PackageContentsViewer extends LightningElement {
  constructor(...args) {
    super(...args);
    this.viewerTitle = '';
    this.searchPlaceholder = '';
    this.rollupTiles = [];
    this.sections = [];
    this.loadingLabel = '';
    this.isLoading = false;
    this.emptySearchLabel = '';
    this.displayListLabel = '';
    this.displayCardsLabel = '';
    this.featureContext = '';
    // Only the no-results empty-state's title/subtitle are wired directly here (rather than
    // taken as @api, like the rest of this component's copy) since both callers already read
    // from this same selector and pass their own subset down as @api props.
    this.labels = void 0;
    // Reactive state must be initialized (not merely declared) for reassignment to
    // re-render under the client build.
    this.searchTerm = '';
    this.viewMode = readInitialViewMode();
  }
  get isCardView() {
    return this.viewMode === 'card';
  }
  get isListView() {
    return this.viewMode === 'list';
  }

  // Single toggle button whose icon/label name the view the click switches TO: "Display List"
  // (list icon) while showing cards, "Display Cards" (apps icon) while showing the list.
  get displayToggleIcon() {
    return this.isListView ? 'apps' : 'list';
  }
  get displayToggleLabel() {
    return this.isListView ? this.displayCardsLabel : this.displayListLabel;
  }
  handleSearchInput(event) {
    const value = event.detail.value;
    this.searchTerm = value;
    this.searchAnalyticsDebounceTimer = debounce(this, this.dispatchSearchAnalytics, [value], this.searchAnalyticsDebounceTimer, SEARCH_ANALYTICS_DEBOUNCE_MS);
  }
  disconnectedCallback() {
    window.clearTimeout(this.searchAnalyticsDebounceTimer);
  }

  // Shape matches ace/filterSearch's dispatchFilterChangeEvent — same elementType/eventType so
  // this search box reports through the existing filterKeywordChange analytics path.
  dispatchSearchAnalytics(value) {
    const searchTerm = value.trim();
    if (!searchTerm) {
      return;
    }
    this.dispatchEvent(new CustomEvent('analytics', {
      detail: {
        value,
        eventType: 'filterKeywordChange',
        elementType: 'filterInput',
        aiAssetAnalytics: aiAssetSearch({
          searchTerm,
          searchResultCount: this.visibleItemCount(searchTerm),
          searchCategory: 'package components'
        })
      },
      composed: true,
      bubbles: true,
      cancelable: true
    }));
  }
  handleDisplayToggleClick() {
    this.viewMode = this.isListView ? 'card' : 'list';
    if (typeof window !== 'undefined') {
      createCatCookie(VIEW_MODE_COOKIE_NAME, this.viewMode, CookieConsentCategory.Functional, VIEW_MODE_COOKIE_EXPIRY_DAYS);
    }
  }
  handleDisplayToggleAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: this.isListView ? 'display cards' : 'display list',
      elementTitle: this.featureContext,
      elementType: 'layout toggle'
    });
  }
  handleItemAnalytics(event) {
    if (event.detail?.eventType !== 'click') {
      return;
    }
    const listing = event.detail.listing;
    if (!listing?.listingUrl) {
      event.stopPropagation();
      return;
    }
    // List rows receive the index directly from analytics-instrument. Card rows receive it
    // from listing-card. Keep the listing fallback for callers using the legacy event shape.
    const positionIndex = event.detail.positionIndex ?? listing?.positionIndex;
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: listing?.title ?? '',
      clickUrl: listing?.listingUrl,
      elementTitle: this.featureContext,
      elementType: this.isCardView ? 'card item' : 'list item',
      position: typeof positionIndex === 'number' ? positionIndex + 1 : undefined,
      pluginResults: this.countItemsByType('ai-plugin'),
      skillResults: this.countItemsByType('ai-skill')
    });
  }

  // Case-insensitive substring match against title, subtitle, and description.
  itemMatchesSearch(item, term) {
    if (!term) {
      return true;
    }
    const haystack = `${item.title} ${item.subtitle} ${item.description}`.toLowerCase();
    return haystack.includes(term);
  }
  toItemViewModel(item, positionIndex) {
    const cardData = {
      ...item.cardData,
      positionIndex
    };
    const isNavigable = item.isNavigable !== false;
    return {
      ...item,
      cardData,
      key: item.id,
      listSubtitle: [item.subtitle, item.description].filter(Boolean).join(' • '),
      hasTypeIcon: Boolean(item.typeIcon),
      hasCategories: Boolean(item.categories?.length),
      isNavigable,
      disableNavigation: !isNavigable,
      typePillClass: this.buildTypePillClass(item.typeIcon),
      analyticsContext: {
        listing: cardData,
        positionIndex
      }
    };
  }

  // Card view badges plugin and skill the same blue, distinguished only by icon glyph; the
  // list-view pill mirrors that with one shared modifier class for both.
  buildTypePillClass(typeIcon) {
    const modifier = typeIcon === PLUGIN_TYPE_ICON || typeIcon === SKILL_TYPE_ICON ? 'type-pill-plugin-skill' : '';
    return ['tag-pill', 'type-pill', modifier].filter(Boolean).join(' ');
  }

  // Filters each section's items by `searchTerm`, dropping sections left with none. Roll-up
  // tiles are unaffected by search — they always show the counts they were given.
  get filteredSections() {
    const term = this.searchTerm.trim().toLowerCase();
    let positionIndex = 0;
    return (this.sections ?? []).map((section, index) => ({
      key: `${section.heading}-${index}`,
      heading: section.heading,
      items: (section.items ?? []).filter(item => this.itemMatchesSearch(item, term)).map(item => this.toItemViewModel(item, positionIndex++))
    })).filter(section => section.items.length > 0);
  }
  visibleItemCount(term) {
    const normalized = term.trim().toLowerCase();
    return (this.sections ?? []).reduce((count, section) => count + (section.items ?? []).filter(item => this.itemMatchesSearch(item, normalized)).length, 0);
  }
  countItemsByType(type) {
    return (this.sections ?? []).reduce((count, section) => count + (section.items ?? []).filter(item => item.cardData.type === type).length, 0);
  }
  get hasFilteredSections() {
    return this.filteredSections.length > 0;
  }
  get hasSearchTerm() {
    return Boolean(this.searchTerm.trim());
  }
  get showEmptySearchMessage() {
    return !this.isLoading && this.hasSearchTerm && !this.hasFilteredSections;
  }
  get showEmptyContentMessage() {
    return !this.isLoading && !this.hasSearchTerm && !this.hasFilteredSections;
  }
  get emptySearchTitle() {
    return this.labels?.emptySearchTitle ?? '';
  }
  get emptySearchSubtitle() {
    return this.labels?.emptySearchSubtitle ?? '';
  }

  // Generic "no results" illustration — no dedicated Specs/Part-Of asset exists, so this
  // reuses the same one skills.ts's landing-page section falls back to.
  get emptySearchImageSrc() {
    return images.errorEmptyState;
  }
  get showContent() {
    return !this.isLoading && !this.showEmptySearchMessage && !this.showEmptyContentMessage;
  }
  get hasRollupTiles() {
    return (this.rollupTiles ?? []).length > 0;
  }

  // The loaded state renders only the primary section (e.g. Found In shows Plugins only, since
  // a skill's parent skills are always 0 today) — mirror just that one heading during loading
  // so nothing shifts when real data arrives.
  get loadingSectionHeading() {
    return this.rollupTiles?.[0]?.label ?? '';
  }
  get cardSkeletons() {
    return this.buildSkeletonPlaceholders(CARD_SKELETON_COUNT);
  }
  get rowSkeletons() {
    return this.buildSkeletonPlaceholders(ROW_SKELETON_COUNT);
  }
  buildSkeletonPlaceholders(count) {
    return Array.from({
      length: count
    }, (_, id) => ({
      id
    }));
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(PackageContentsViewer, {
  publicProps: {
    viewerTitle: {
      config: 0
    },
    searchPlaceholder: {
      config: 0
    },
    rollupTiles: {
      config: 0
    },
    sections: {
      config: 0
    },
    loadingLabel: {
      config: 0
    },
    isLoading: {
      config: 0
    },
    emptySearchLabel: {
      config: 0
    },
    displayListLabel: {
      config: 0
    },
    displayCardsLabel: {
      config: 0
    },
    featureContext: {
      config: 0
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills.packageContents
        };
      }
    }
  },
  fields: ["searchTerm", "viewMode"]
});
const __lwc_component_class_internal = _registerComponent(PackageContentsViewer, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;